import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Label,
} from "recharts";
import ThinScroll from "../../shared/ThinScroll";
import ThemedChartTooltip from "../../shared/ThemedChartTooltip";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportBookingWindow } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import { PanelTakeaway, fmtRate, SERIES_HUES } from "../shared";
import { axisProps, axisLabelStyle } from "../shared/chartTheme";

// Spec comparison palette: you=blue, market=emerald. Hues from shared SERIES_HUES.
const COLOR_YOU = SERIES_HUES.blue;
const COLOR_MARKET = SERIES_HUES.emerald;

const BookingWindowBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No booking-window data for the selected category." />;
  }

  const chartData = rows.map((row) => ({
    bucket: row.booking_window,
    your: row.your_avg_price ?? 0,
    market: row.market_avg_price ?? 0,
  }));

  return (
    <div className="p-5 space-y-5">
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
            <XAxis
              dataKey="bucket"
              {...axisProps}
            />
            <YAxis
              {...axisProps}
              tickFormatter={(value) => `$${value}`}
            >
              <Label
                value="Rate (USD)"
                angle={-90}
                position="insideLeft"
                offset={10}
                style={axisLabelStyle}
              />
            </YAxis>
            <ThemedChartTooltip
              formatter={(value) => fmtRate(value)}
              itemSorter={(item) => (item.dataKey === "your" ? 0 : 1)}
            />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              itemSorter={(item) => (item.dataKey === "your" ? 0 : 1)}
            />
            <Bar dataKey="your" name="Your Avg" fill={COLOR_YOU} radius={[2, 2, 0, 0]} />
            <Bar dataKey="market" name="Market Avg" fill={COLOR_MARKET} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Window</th>
              <th className="px-3 py-2.5 text-right font-semibold">Dates</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail.</th>
              <th className="px-3 py-2.5 text-right font-semibold">Comp Occupancy</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Occupancy</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.booking_window} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Window" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{row.booking_window}</td>
                <td data-label="Dates" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.dates_in_window ?? 0}</td>
                <td data-label="Your Avg" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: COLOR_YOU }}>{fmtRate(row.your_avg_price)}</td>
                <td data-label="Market Avg" className="px-3 py-2.5 text-right font-semibold whitespace-nowrap" style={{ color: COLOR_MARKET }}>{fmtRate(row.market_avg_price)}</td>
                <td data-label="Limited Avail." className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.competitors_sold_out ?? 0}</td>
                <td data-label="Comp Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.comp_avg_occupancy} /></td>
                <td data-label="Your Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.your_avg_occupancy} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>
    </div>
  );
};

const BookingWindowPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`booking-window-${clientId}-${categoryCode || "all"}`}
    title="Booking Window Analysis"
    tip="Buckets dates by how far out check-in is (0–7d, 8–14d, etc.) and shows how your pricing and the market's evolve as the booking window closes — usually prices firm up as the date approaches."
    description="How pricing and demand shift as check-in approaches. Compare your avg vs market across booking horizons."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportBookingWindow(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? [...data].sort((rowA, rowB) => (rowA.bucket_order ?? 0) - (rowB.bucket_order ?? 0)) : [];
      return (
        <>
          <PanelTakeaway panelKey="booking_window" say={say} />
          <BookingWindowBody rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default BookingWindowPanel;
